import React, { useEffect, useState } from "react";
import { Heart, Loader2, Bookmark, Trash2, Music2, Music4, GripVertical, ArrowUpDown, Check, Radio } from "lucide-react";
import { Reorder } from "framer-motion";
import StationCard from "@/components/StationCard";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Favorites() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowPlaying, setNowPlaying] = useState({});
  const [songs, setSongs] = useState([]);
  const [listeners, setListeners] = useState({});
  const [reordering, setReordering] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = () => api.get("/favorites").then((res) => { setStations(res.data); setLoading(false); }).catch(() => setLoading(false));
  const loadSongs = () => api.get("/saved-songs").then((res) => setSongs(res.data)).catch(() => {});
  useEffect(() => { load(); loadSongs(); }, []);

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.put("/favorites/order", { station_ids: stations.map((s) => s.id) });
      toast.success("Order saved");
      setReordering(false);
    } catch (e) { toast.error("Could not save order"); } finally { setSavingOrder(false); }
  };

  const delSong = async (id) => {
    try { await api.delete(`/saved-songs/${id}`); setSongs((p) => p.filter((s) => s.id !== id)); toast.success("Removed"); }
    catch (e) { toast.error("Could not remove"); }
  };
  const requestSong = async (s) => {
    try {
      await api.post("/requests", { song: s.song, artist: s.artist || "", message: "Re-requested from Saved Songs", station_id: null });
      toast.success(`Requested "${s.song}" 🎶`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
  };

  useEffect(() => {
    const fetchNow = () => api.get("/stations/nowplaying").then((res) => {
      const map = {};
      Object.entries(res.data || {}).forEach(([id, v]) => { if (v && v.title) map[id] = { title: v.title, artwork: v.artwork }; });
      setNowPlaying(map);
    }).catch(() => {});
    fetchNow();
    const t = setInterval(fetchNow, 25000);
    const fetchListeners = () => api.get("/stations/listeners").then((res) => setListeners(res.data || {})).catch(() => {});
    fetchListeners();
    const lt = setInterval(fetchListeners, 20000);
    return () => { clearInterval(t); clearInterval(lt); };
  }, []);

  const toggleFav = async (station) => {
    try {
      await api.post(`/favorites/${station.id}`);
      setStations((prev) => prev.filter((s) => s.id !== station.id));
      toast.success("Removed from favorites");
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-3">
          <Heart className="w-5 h-5 text-[#FF3B30] fill-[#FF3B30]" />
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Your Favorites</h1>
        </div>
        {stations.length > 1 && (
          reordering ? (
            <button onClick={saveOrder} disabled={savingOrder} data-testid="favorites-save-order" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
              {savingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Done
            </button>
          ) : (
            <button onClick={() => setReordering(true)} data-testid="favorites-reorder" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 text-zinc-200 text-sm font-semibold hover:bg-white/10 transition-colors">
              <ArrowUpDown className="w-4 h-4" /> Reorder
            </button>
          )
        )}
      </div>
      <p className="text-zinc-400 mb-8">{reordering ? "Drag stations into the order you like, then tap Done." : "Stations you love, always one tap away."}</p>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>
      ) : stations.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl" data-testid="favorites-empty">
          <Heart className="w-10 h-10 mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-400">No favorites yet. Tap the heart on any station to save it here.</p>
        </div>
      ) : reordering ? (
        <Reorder.Group axis="y" values={stations} onReorder={setStations} className="flex flex-col gap-3 max-w-2xl" data-testid="favorites-reorder-list">
          {stations.map((s) => (
            <Reorder.Item
              key={s.id}
              value={s}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#141C42] p-3 cursor-grab active:cursor-grabbing select-none touch-none"
              whileDrag={{ scale: 1.03, boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}
              data-testid={`reorder-item-${s.id}`}
            >
              <GripVertical className="w-5 h-5 text-zinc-500 shrink-0" />
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#0C1338] shrink-0 flex items-center justify-center">
                {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> : <Radio className="w-5 h-5 text-[#E6B34A]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold truncate">{s.name}</p>
                <p className="text-xs text-zinc-400 truncate">{s.genre}{s.frequency ? ` · ${s.frequency}` : ""}</p>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="favorites-grid">
          {stations.map((s, i) => (
            <StationCard key={s.id} station={s} onToggleFav={toggleFav} index={i} nowPlaying={nowPlaying[s.id]} listeners={listeners[s.id] || 0} />
          ))}
        </div>
      )}

      {songs.length > 0 && (
        <div className="mt-14" data-testid="saved-songs">
          <div className="flex items-center gap-3 mb-1">
            <Bookmark className="w-5 h-5 text-[#E6B34A] fill-[#E6B34A]" />
            <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight">Saved Songs</h2>
          </div>
          <p className="text-zinc-400 mb-6">Tracks you tapped to keep while listening.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {songs.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-[#141C42] p-3 group" data-testid={`saved-song-${s.id}`}>
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#0C1338] shrink-0 flex items-center justify-center">
                  {s.artwork ? <img src={s.artwork} alt="" className="w-full h-full object-cover" /> : <Music2 className="w-6 h-6 text-[#E6B34A]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">{s.song}</p>
                  {s.artist && <p className="text-xs text-zinc-400 truncate">{s.artist}</p>}
                  {s.station_name && <p className="text-[10px] text-zinc-600 truncate mt-0.5">on {s.station_name}</p>}
                </div>
                <button onClick={() => requestSong(s)} data-testid={`request-song-${s.id}`} title="Request this song on air" className="p-2 rounded-lg text-[#E6B34A] hover:bg-[#E6B34A]/10 transition-colors shrink-0"><Music4 className="w-4 h-4" /></button>
                <button onClick={() => delSong(s.id)} data-testid={`del-song-${s.id}`} className="p-2 rounded-lg text-zinc-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
